import type { Dictionary, Image, StaticInfoType } from '../general';

import type { HotelAmenities } from './getHotels';

export interface ProductStaticInfoMap {
  [StaticInfoType.Overview]: OverviewHotel;
  [StaticInfoType.OverviewActivity]: OverviewActivity;
  [StaticInfoType.OverviewHeartedHotel]: OverviewHeartedHotel;
  [StaticInfoType.Destination]: DestinationHotel;
  [StaticInfoType.Details]: DetailsHotel;
  [StaticInfoType.NameOnly]: NameOnlyHotel;
  [StaticInfoType.MainPolygonNameOnly]: MainPolygonNameOnlyHotel;
  [StaticInfoType.PositionOnly]: PositionOnlyHotel;
  [StaticInfoType.ActivitySuggestions]: ActivitySuggestionsHotel;
}

export interface OverviewHotel {
  hotelCode: string;
  name: string;
  address: Address;
  images: Image[];
  semboSunRating: FullStars;
  position: Position;
  typeOfHotel: AccommodationType;
  mainPolygon?: MainPolygon;
}

export interface OverviewActivity {
  hotelCode: string;
  name: string;
  images: Image[];
  semboSunRating: FullStars;
  typeOfHotel: AccommodationType;
}

export interface OverviewHeartedHotel extends OverviewHotel {
  polygonIds: number[];
}

export interface DestinationHotel {
  hotelCode: string;
  name: string;
  images: Image[];
  semboSunRating: FullStars;
  position: Position;
  popularityScore: number;
  polygonIds: number[];
  typeOfHotel: AccommodationType;
  mainPolygon?: MainPolygon;
  address?: Address;
  openingParagraph?: OpeningParagraph;
}

export interface DetailsHotel {
  hotelCode: string;
  name: string;
  description: Description;
  openingParagraph: Omit<OpeningParagraph, 'isAiGenerated'>;
  address: Address;
  images: Image[];
  properties: Dictionary<ValueWithDescription>;
  semboSunRating: FullStars;
  position: Position;
  distances: Dictionary<ValueWithDescription<number>>;
  polygonIds: number[];
  typeOfHotel: AccommodationType;
  tags: Tag[];
  amenities?: HotelAmenities;
}

export interface NameOnlyHotel {
  name: string;
}

export interface MainPolygonNameOnlyHotel {
  mainPolygon?: {
    fullName: string;
  };
}

export interface PositionOnlyHotel {
  position: Position;
}

export interface ActivitySuggestionsHotel {
  hotelCode: string;
  name: string;
  description: Description;
  openingParagraph: Omit<OpeningParagraph, 'isAiGenerated'>;
  images: Image[];
  popularityScore: number;
}

export interface Description {
  twoLetterIsoLanguageCode: string | null;
  value: string | null;
}

interface OpeningParagraph {
  twoLetterIsoLanguageCode: string;
  value: string;
  isAiGenerated?: boolean;
}

export interface Address {
  city: string;
  country: string;
}

interface ValueWithDescription<T = unknown> {
  description: string;
  value: T;
}

interface Position {
  longitude: ServerCoordinate;
  latitude: ServerCoordinate;
}

interface ServerCoordinate {
  decimalDegrees: number;
}

export enum AccommodationRule {
  FreeCancellation = 'FreeCancellation',
}

export enum AccommodationType {
  Hotel = 'Hotel',
  Apartment = 'Apartment',
  HouseOrCottage = 'HouseOrCottage',
  Ticket = 'Ticket',
  Golf = 'Golf',
}

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export type StarsRatingFilter = FullStars | 0;

export enum AmenitiesFilter {
  Pool = 'Pool',
  Parking = 'Parking',
  Spa = 'Spa',
  Gym = 'Gym',
  Wifi = 'Wifi',
  AirConditioned = 'AirConditioned',
  VehicleChargingStation = 'VehicleChargingStation',
}

export enum ExperienceFilter {
  Business = 'Business',
  Family = 'Family',
  PetsAllowed = 'PetsAllowed',
}

export enum BoardBasisFilter {
  RoomOnly = 'RoomOnly',
  Breakfast = 'Breakfast',
  HalfBoard = 'HalfBoard',
  FullBoard = 'FullBoard',
  AllInclusive = 'AllInclusive',
}

export const HOTEL_RULES: AccommodationRule[] = [
  AccommodationRule.FreeCancellation,
];

export const HOTEL_TYPES: AccommodationType[] = [
  AccommodationType.Hotel,
  AccommodationType.Apartment,
  AccommodationType.HouseOrCottage,
];

export const OLD_POPULAR_FILTERS = [
  AmenitiesFilter.Pool,
  AmenitiesFilter.Parking,
  ExperienceFilter.PetsAllowed,
];

export const ACTIVITY_TYPES: AccommodationType[] = [
  AccommodationType.Ticket,
  AccommodationType.Golf,
];

export const AMENITIES_FILTERS: AmenitiesFilter[] = [
  AmenitiesFilter.Pool,
  AmenitiesFilter.Parking,
  AmenitiesFilter.Spa,
  AmenitiesFilter.Gym,
  AmenitiesFilter.Wifi,
  AmenitiesFilter.AirConditioned,
  AmenitiesFilter.VehicleChargingStation,
];

export const BOARD_BASIS_FILTERS: BoardBasisFilter[] = [
  BoardBasisFilter.RoomOnly,
  BoardBasisFilter.Breakfast,
  BoardBasisFilter.HalfBoard,
  BoardBasisFilter.FullBoard,
  BoardBasisFilter.AllInclusive,
];

export const EXPERIENCE_FILTERS: ExperienceFilter[] = [
  ExperienceFilter.Business,
  ExperienceFilter.Family,
  ExperienceFilter.PetsAllowed,
];

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const FULL_STAR_LIST = [1, 2, 3, 4, 5] as const;

export type FullStars = (typeof FULL_STAR_LIST)[number];

export interface Tag {
  name: string;
}

interface MainPolygon {
  id: number;
  fullName: string;
}

export interface HeartedHotel {
  code: string;
  roomCodes?: string[];
  isRefundable?: boolean;
  rooms?: HeartedRoom[];
}

export interface HeartedRoom {
  code: string;
  boardBasis: string;
  roomTypeId?: number;
}
